'use strict';
(function(exports) {
	/**
	 * @interface DataWrapper
	 *
	 * The common interface that the other DataWrappers use.  This is validated
	 * by the unit tests.
	 *
	 * Note: it is not done as a superclass (as {@link PitchMapper} is) because
	 *       there's really nothing in common implementation-wise; only the
	 *       interface is shared.
	 *
	 * @private
	 */
	/**
	 * Returns the number of series in the underlying data
	 * @function DataWrapper#numSeries
	 * @returns {integer} The number of series
	 */
	/**
	 * Get a list of the underlying data series names
	 * @function DataWrapper#seriesNames
	 * @returns {string[]} An array of the series names
	 */
	/**
	 * What is the minimum value in a given series?
	 * @function DataWrapper#seriesMin
	 * @param {index} series - The series number
	 * @returns {Number} The minimum value in the series
	 */
	/**
	 * What is the maximum value in a given series?
	 * @function DataWrapper#seriesMax
	 * @param {index} series - The series number
	 * @returns {Number} The maximum value in the series
	 */
	/**
	 * Get value of specific datum
	 * @function DataWrapper#seriesValue
	 * @param {index} series - The series number
	 * @param {index} index - The row number
	 * @todo rename `index` to `row`?
	 * @returns {Number} the datum
	 */
	/**
	 * What is the length of a series?
	 * @function DataWrapper#seriesLength
	 * @param {index} series - The series
	 * @returns {integer} The number of data in the series
	 */


	/**
	 * This interfaces to Google's {@link https://developers.google.com/chart/interactive/docs/reference#DataTable|DataTable} class.
	 *
	 * @private
	 * @implements {DataWrapper}
	 * @param {GoogleDataTable} data - The in-memory GoogleDataTable
	 */
	class GoogleDataWrapper {
		constructor(data) {
			this.data = data
		}

		numSeries() {
			let numberOfDataColumns = 0

			// Check the role of each column
			// Note: the first, domain, column, isn't counted
			for (let i = 1; i < this.data.getNumberOfColumns(); i++) {
				const role = this.data.getColumnRole(i)
				if (role === '' || role === 'data') {  // TODO 'data' used?
					numberOfDataColumns += 1
				}
			}

			return numberOfDataColumns
		}

		seriesNames() {
			const results = []
			for (let i = 0; i < this.data.getNumberOfColumns() - 1; i++) {
				results.push(this.data.getColumnLabel(i))
			}
			return results
		}

		seriesMin(series) {
			return this.data.getColumnRange(series + 1).min
		}

		seriesMax(series) {
			return this.data.getColumnRange(series + 1).max
		}

		seriesValue(series, index) {
			return this.data.getValue(index, series + 1)
		}

		seriesLength(series) {
			return this.data.getNumberOfRows()
		}
	}


	/**
	 * This allows a JSON fragment to be used as a data source.
	 *
	 * @todo document format
	 *
	 * @private
	 * @implements {DataWrapper}
	 * @param {JSON} json - The JSON data, as a string or object
	 */
	class JSONDataWrapper {
		constructor(json) {
			if (typeof json === 'string') {
				this.object = JSON.parse(json)
			} else if (typeof json === 'object') {
				this.object = json
			} else {
				throw Error('Please provide a JSON string or derived object.')
			}
		}

		numSeries() {
			return this.object.data.length
		}

		seriesNames() {
			const results = []
			for (let i = 0; i < this.object.data.length; i++) {
				results.push(this.object.data[i].series)
			}
			return results
		}

		seriesMin(series) {
			return Math.min.apply(this, this.object.data[series].values)
		}

		seriesMax(series) {
			return Math.max.apply(this, this.object.data[series].values)
		}

		seriesValue(series, index) {
			return this.object.data[series].values[index]
		}

		seriesLength(series) {
			return this.object.data[series].values.length
		}
	}


	/**
	 * Support C3-format data objects
	 *
	 * @todo document format
	 *
	 * @private
	 * @implements {DataWrapper}
	 * @param {Object} data - The C3-format data object
	 */
	class C3DataWrapper {
		constructor(data) {
			if (typeof data === 'object') {
				this.object = data
			} else {
				throw Error('Please provide a C3-format data object.')
			}
		}

		numSeries() {
			let numberOfSeries = this.object.columns.length
			if (this.object.hasOwnProperty('x')) {
				numberOfSeries -= 1
			}
			return numberOfSeries
		}

		seriesNames() {
			const results = []
			for (let i = 0; i < this.object.columns.length; i++) {
				results.push(this.object.columns[i][0])
			}
			return results
		}

		seriesMin(series) {
			return Math.min.apply(this, this.object.columns[series].slice(1))
		}

		seriesMax(series) {
			return Math.max.apply(this, this.object.columns[series].slice(1))
		}

		seriesValue(series, index) {
			return this.object.columns[series][index + 1]
		}

		seriesLength(series) {
			return this.object.columns[series].length - 1
		}
	}


	/**
	 * Allows an HTML table to be used as a data source.
	 *
	 * @private
	 * @implements {DataWrapper}
	 * @param {HTMLTableElement} table - The in-DOM table element
	 * @todo check it's a table
	 */
	class HTMLTableDataWrapper {
		constructor(table) {
			this.table = table
			if (!this.table) {
				throw Error('No table given.')
			}
		}

		numSeries() {
			return this.table.getElementsByTagName('tr')[0].children.length
		}

		seriesNames() {
			return Array.from(
				this.table.getElementsByTagName('th'),
				(cell) => cell.textContent)
		}

		_seriesFloats(series) {
			return Array.from(
				this.table.querySelectorAll(`td:nth-child(${series + 1})`),
				(cell) => parseFloat(cell.textContent))
		}

		seriesMin(series) {
			return Math.min.apply(this, this._seriesFloats(series))
		}

		seriesMax(series) {
			return Math.max.apply(this, this._seriesFloats(series))
		}

		seriesValue(series, index) {
			return parseFloat(
				this.table.getElementsByTagName('tr')[index + 1]
					.children[series].textContent)
		}

		seriesLength(series) {
			return this.table.getElementsByTagName('tr').length - 1
		}
	}

	/**
	 * Base Pitch Mapper
	 * @private
	 * @abstract
	 */
	class PitchMapper {
		/**
		 * Create a base Pitch Mapper
		 * @param {Object[]} seriesInfo - min and max data and frequencies
		 * @param {string} seriesInfo[].minimumDatum - minimun data value
		 * @param {string} seriesInfo[].maximumDatum - maximun data value
		 * @param {string} seriesInfo[].minimumFrequency - minimun frequency
		 * @param {string} seriesInfo[].maximumFrequency - maximun frequency
		 */
		constructor(seriesInfo) {
			// Store the info for all series
			this._minimumDatum = []
			this._maximumDatum = []
			this._minimumFrequency = []
			this._maximumFrequency = []
			this._dataRange = []
			this._frequencyRange = []

			seriesInfo.forEach((series, index) => {
				if (series.maximumDatum < series.minimumDatum) {
					throw Error('minimum datum should be <= maximum datum')
				}

				if (series.maximumFrequency < series.minimumFrequency) {
					throw Error('minimum frequency should be <= maximum frequency')
				}

				this._minimumDatum[index] = series.minimumDatum
				this._maximumDatum[index] = series.maximumDatum
				this._minimumFrequency[index] = series.minimumFrequency
				this._maximumFrequency[index] = series.maximumFrequency

				// Pre-calculate for performance (I expect) when mapping
				this._dataRange[index] =
					this._maximumDatum[index] - this._minimumDatum[index]
				this._frequencyRange[index] =
					this._maximumFrequency[index] - this._minimumFrequency[index]
			})
		}

		/**
		 * Map a datum to an output value
		 * @param {number} datum - the datum to be mapped
		 * @abstract
		 */
		map(datum) {
			throw Error('Base map() must be overriden.')
		}
	}


	/**
	 * Maps each datum to a corresponding frequency.
	 * @private
	 * @extends {PitchMapper}
	 */
	class FrequencyPitchMapper extends PitchMapper {
		// TODO: dupe docs for now: https://github.com/jsdoc3/jsdoc/issues/1012
		/**
		 * Create a Frequency Pitch Mapper
		 * @param {Object[]} seriesInfo - minimum and maximums for each series
		 * @param {string} seriesInfo[].minimumDatum - minimun data value
		 * @param {string} seriesInfo[].maximumDatum - maximun data value
		 * @param {string} seriesInfo[].minimumFrequency - minimun frequency
		 * @param {string} seriesInfo[].maximumFrequency - maximun frequency
		 */
		constructor(seriesInfo) {
			super(seriesInfo)
		}

		/**
		 * Given a datum value, compute the corresponding frequency
		 * @param {number} series - map on which series?
		 * @param {number} datum - the datum to be mapped
		 * @returns {number} frequency for this datum
		 */
		map(series, datum) {
			let ratio
			if (this._dataRange[series]) {
				ratio = (datum - this._minimumDatum[series])
					/ this._dataRange[series]
			} else {
				ratio = 0.5
			}
			return this._minimumFrequency[series]
				+ (ratio * this._frequencyRange[series])
		}
	}

	/**
	 * Outputs sound using the Web Audio API
	 * @private
	 * @param {AudioContext} context - the Web Audio API context
	 * @param {number} numberOfSeries - number of data series
	 */
	class Sounder {
		constructor(context, numberOfSeries) {
			if (context === undefined) {
				throw Error('No audio context given')
			}
			this._context = context

			if (numberOfSeries === undefined) {
				throw Error('No number of data series given')
			} else if (numberOfSeries > 2) {
				throw Error('Large number of data series given')
			}
			this._numberOfSeries = numberOfSeries
		}

		/**
		 * Start the oscillator(s)
		 */
		start() {
			// Oscillators cannot be re-used
			this._oscillators = []
			for (let i = 0; i < this._numberOfSeries; i++) {
				this._oscillators[i] = this._context.createOscillator()
				if (i === 1) this._oscillators[i].type = 'square'
				this._oscillators[i].connect(this._context.destination)
				this._oscillators[i].start(0)
			}
		}

		/**
		 * Set the frequency of the oscillator for a given series
		 * @param {number} series - set frequency for which series?
		 * @param {number} frequency - the frequency to change to (Hz)
		 */
		frequency(series, frequency) {
			this._oscillators[series].frequency.value = frequency
		}

		/**
		 * Stop the oscillator(s)
		 */
		stop() {
			this._oscillators.forEach((oscillator) => oscillator.stop())
		}
	}

	/**
	 * Orchestrates the audible (and visual cursor) rendering of the chart
	 * @private
	 * @param {integer} duration - the length of the rendering in milliseconds
	 * @param {DataWrapper} data - the underlying data (wrapped in interface)
	 * @param {PitchMapper} pitchMapper - maps data to pitches
	 * @param {Sounder} sounder - the sounder object
	 * @param {VisualCallback} visualCallback - the callback function that highlights the current datum
	 */
	class Player {
		constructor(duration, data, pitchMapper, sounder, visualCallback) {
			this.data = data
			this.pitchMapper = pitchMapper
			this.sounder = sounder
			if (arguments.length < 5) {
				this.visualCallback = null
			} else {
				this.visualCallback = visualCallback
			}

			const seriesLen = this.data.seriesLength(0)

			this._numberOfSeries = this.data.numSeries()

			const sampling = Player._samplingInfo(duration, seriesLen)
			this.interval = sampling.interval
			this.sampleOneIn = sampling.in

			this.seriesMaxIndex = seriesLen - 1  // TODO just use seriesLen?
			this._state = 'ready'
		}

		/**
		 * Main entry point; manages state.
		 */
		playPause() {
			switch (this._state) {
				case 'ready':
					this._play()
					break
				case 'playing':
					this._pause()
					break
				case 'paused':
					this._playLoop()
					break
				case 'finished':
					this._play()
					break
				default:
					throw Error('Player error: invalid state: ' + String(this._state))
			}
		}

		stepBackward(skip) {
			const delta = skip || 50
			this.playIndex -= delta
			if (this.playIndex < 0) {
				this.playIndex = 0  // TODO test limiting
			}
			if (this._state === 'paused') {
				this._playOne()
			}
		}

		stepForward(skip) {
			const delta = skip || 50
			this.playIndex += delta
			if (this.playIndex > this.seriesMaxIndex) {
				this.playIndex = this.seriesMaxIndex  // TODO test limiting
			}
			if (this._state === 'paused') {
				this._playOne()
			}
		}

		/**
		 * Resets play state and sets up a recurring function to update the sound
		 * (and, optionally, visual callback) at an interval dependant on the
		 * number of data.
		 */
		_play() {
			// Debugging info
			this.playTimes = []  // store all lengths of time that playOne took
			this.playCount = 0   // how many datum points were actually sounded?

			this.startTime = performance.now()
			this.sounder.start(0)
			this.playIndex = 0

			this._playLoop()
		}

		/**
		 * Update state and set _playOne() to run regularly, to render the sound
		 * (and optional visual cursor movement).
		 */
		_playLoop() {
			this._state = 'playing'
			this._playOne()  // so that it starts immediately
			this.intervalID = setInterval(() => this._playOne(), this.interval)
		}

		/**
		 * This is where the sound is actually played.  If a visual callback was
		 * specified, this also coordinates the visual highlighting of the current
		 * datum as the playback occurs.
		 */
		_playOne() {
			const thisPlayTimeStart = performance.now()

			if (this.playIndex <= this.seriesMaxIndex) {
				// Play back one datum point
				if (this.visualCallback !== null) {
					this.visualCallback(this.playIndex)
				}
				for (let i = 0; i < this._numberOfSeries; i++ ) {
					this.sounder.frequency(i, this.pitchMapper.map(i,
						this.data.seriesValue(i, this.playIndex)))
				}
			} else {
				// Playback is complete; clean up
				clearInterval(this.intervalID)
				this.sounder.stop()
				this._state = 'finished'

				// Debugging info
				// console.log(`Player: Playing ${this.playCount} of ${this.playIndex} took ${Math.round(performance.now() - this.startTime)} ms`)
				const sum = this.playTimes.reduce((acc, cur) => acc + cur)
				const mean = sum / this.playTimes.length
				// console.log(`Player: Average play func time: ${mean.toFixed(2)} ms`)
			}

			this.playIndex += this.sampleOneIn > 0 ? this.sampleOneIn : 1  // TODO sl
			this.playCount += 1
			this.playTimes.push(performance.now() - thisPlayTimeStart)
		}

		/**
		 * Temporarily pause the rendering of the chart.
		 * This inherently keeps the sound going at the frequency it was at when
		 * the pause was triggered.
		 * @todo feature/object to stop/fade the sound after n seconds?
		 */
		_pause() {
			clearInterval(this.intervalID)
			this._state = 'paused'
		}

		/* Work out sampling rate */
		static _samplingInfo(duration, seriesLen) {
			const minInterval = 10
			let interval
			let sampleOneIn
			let slots

			const idealInterval = Math.ceil(duration / seriesLen)
			// console.log(`sampleInfo: duration: ${duration}; series length: ${seriesLen}; ideal interval: ${idealInterval}`)

			if (idealInterval < minInterval) {
				interval = minInterval
				slots = Math.floor(duration / minInterval)
				const sampleOneInFloat = seriesLen / slots
				sampleOneIn = Math.round(seriesLen / slots)
				// console.log(`sampleInfo: Need to sample 1 in ${sampleOneIn} (${sampleOneInFloat})`)
			} else {
				slots = Math.floor(duration / minInterval)
				interval = idealInterval
				sampleOneIn = 1
			}

			// console.log(`sampleInfo: it will take ${ (seriesLen / sampleOneIn) * interval}`)

			return {
				'sample': 1,
				'in': sampleOneIn,
				'interval': interval
			}
		}
	}

	/**
	 * Ensures that there is only one Web Audio context per page.
	 * Sets up a new AudioContext the first time it's called; then re-uses it.
	 * @private
	 * @returns {AudioContext} page-global Web Audio context
	 */
	var getAudioContext = (function() {
		let audioContext = null

		if (window.AudioContext !== undefined) {
			audioContext = new window.AudioContext()
		} else if (window.webkitAudioContext !== undefined) {
			/* eslint-disable new-cap */
			audioContext = new window.webkitAudioContext()
			/* eslint-enable new-cap */
		}

		function _getAudioContext() {
			return audioContext
		}

		return _getAudioContext
	})()

	/**
	 * Generates a function that moves the cursor on a Google Chart
	 * @private
	 * @param {GoogleChart} chart - the in-memory GoogleChart object
	 * @returns {VisualCallback} the callback
	 */
	var googleVisualCallbackMaker = function(chart) {
		return function(row) {
			chart.setSelection([
				{
					'row': row
				}
			])
		}
	}


	/**
	 * Generate a function that can be used to highlight table cells
	 * @private
	 * @param {HTMLTableElement} table - The in-DOM table element
	 * @param {string} className - Name of the CSS highlight class
	 * @returns {VisualCallback} The highlighting function
	 */
	var htmlTableVisualCallbackMaker = function(table, className) {
		return function(row) {
			const rows = table.getElementsByTagName('tr')

			for (const row of rows) {
				row.classList.remove(className)
			}

			rows[row + 1].classList.add(className)
		}
	}


	/**
	 * Generates a function that moves the cursor on a C3 Chart
	 * @private
	 * @param {Object} chart - the in-memory C3 chart object
	 * @returns {VisualCallback} the callback
	 * @todo define C3 chart type?
	 */
	var c3VisualCallbackMaker = function(chart) {
		return function(row) {
			chart.select(null, [row], true)
		}
	}

	/**
	 * Set up a keyboard event listener to detect chart navigation keypresses.
	 * @private
	 * @param {HTMLElement} container
	 *	- The element (usually a <code>&lt;div&gt;</code>) containing the chart
	 * @param {Player} player - AudioChart Player object
	 * @todo mark up the DIV properly
	 * @todo check what sort of element we get given? no; could be button?
	 */
	class KeyboardHandler {
		constructor(container, player) {
			if (!container) {
				throw Error('No container given')
			}
			if (!player) {
				throw Error('No Player given')
			}

			container.setAttribute('tabindex', '0')
			container.addEventListener('keydown', this.keypressHandler.bind(this))
			this.player = player
		}

		/**
		 * Handle keypresses
		 *
		 * Note: This is bound to the {@link KeyboardHandler} so that it can call
		 *       the right handler methods.
		 *
		 * @param {KeyboardEvent} event - the KeyboardEvent that occured
		 * @todo make link work
		 */
		keypressHandler(event) {
			event.preventDefault()  // TODO should this be here or later? check for defaultPrevented?

			if (event.key === 'ArrowRight') {
				this.handleRight()
			} else if (event.key === 'ArrowLeft' ) {
				this.handleLeft()
			} else if (event.key === ' ') {
				this.handleSpace()
			}
		}

		/** Handle a left arrow being pressed */
		handleLeft() {
			this.player.stepBackward()
		}

		/** Handle a right arrow being pressed */
		handleRight() {
			this.player.stepForward()
		}

		/** Handle the space key being pressed */
		handleSpace() {
			this.player.playPause()
		}
	}

	/** @module */
	/* exported AudioChart */
	/* global getAudioContext FrequencyPitchMapper Sounder Player KeyboardHandler GoogleDataWrapper googleVisualCallbackMaker JSONDataWrapper HTMLTableDataWrapper htmlTableVisualCallbackMaker C3DataWrapper c3VisualCallbackMaker */

	/**
	 * Array index number (starts at zero).
	 * Used to specify series and row in visual callbacks.
	 * @typedef {integer} index
	 */


	/**
	 * A function that highlights the current datum, in all series, visually.
	 * Different callbacks must be created for different types of chart.
	 * @callback VisualCallback
	 * @param {index} row - The row of the cell to highlight
	 */

	/**
	 * @typedef {Object} AudioChartOptions
	 * @todo move the documentation here? (Downside is that the branching/groups
	 *       of different options required for different chart types would be less
	 *       clear.)
	 */

	/**
	 * @typedef {Object} WrapperAndCallbackResults
	 * @property {Class} WrapperClass - the data wrapper class
	 * @property {Object|HTMLTableElement} dataSource - the data source to wrap
	 * @property {VisualCallback} visualCallback - created if requested by the user
	 */

	/** Main object for API consumers */
	class AudioChart {
		/**
		 * Create an AudioChart object.
		 * This first checks to see if the Web Audio API is available, and throws
		 * an {Error} if not. Then check the options given by the user.
		 * @param {AudioChartOptions} options - AudioChart options
		 */
		constructor(options) {
			const context = getAudioContext()
			if (context === null) {
				throw Error(
					"Sorry, your browser doesn't support the Web Audio API.")
			}
			this._setUp(context, options)
		}

		/**
		 * Passes through play/pause commands to the Player
		 */
		playPause() {
			this.player.playPause()
		}

		/**
		 * Returns the current set of options (passed in at object creation, or
		 * computed when options were updated).
		 */
		get options() {
			return this._options
		}

		/**
		 * Updates an AudioChart object to reflect new options. Can accept a subset
		 * of the standard options, so if only, for example, duration changes, then
		 * you need only specify the new duration and not the type and other
		 * paramaters.
		 * @param {AudioChartOptions} newOptions - Partial/full AudioChart options
		 */
		updateOptions(newOptions) {
			if (newOptions === undefined || Object.keys(newOptions).length === 0) {
				throw Error('No new options given')
			}
			const patchedOptions = Object.assign({}, this._options, newOptions)
			this._setUp(getAudioContext(), patchedOptions)
		}

		/**
		 * Checks options (either when a new AudioChart object is created, or when
		 * the user has asked for them to be updated) and then set everything up.
		 * @param {AudioContext} context - the Web Audio context
		 * @param {AudioChartOptions} options - AudioChart options
		 * @private
		 */
		_setUp(context, options) {
			// The testing for this next bit is a bit of a fudge as curerntly I've
			// not come up with a beter way than having the testing done on static
			// functions and checking that they've been called with appropraite
			// values, return appropriate values, or if they throw an exception.
			//
			// The thing blocking this is that I don't know how to stub out global
			// ES6 classes/functions *or* how to run each test via Karma in an
			// isolated environment where I can mock those global classes.
			//
			// TODO, as per https://github.com/matatk/audiochart/issues/37

			// Testing this separately allows us to check the options-checking code
			// without having to pass in functioning source data objects.
			AudioChart._checkOptions(options)

			// This is not currently tested; to mitigate, it doesn't make decisions.
			// Actually, it does call _assignWrapperCallback() but that /is/ tested.
			this._wireUpStuff(context, options)

			// Re _assignWrapperCallback(): that is also tested separately to avoid
			// the need for very detailed mocks for the data sources.

			this._options = Object.freeze(options)
		}

		/**
		 * Checks the passed-in options opbject for validity. This does not perform
		 * detailed checks that are covered in the various components' constructors;
		 * they run such checks themselves.
		 * @param {AudioChartOptions} options - AudioChart options
		 * @private
		 */
		static _checkOptions(options) {
			if (!options.hasOwnProperty('duration')) {
				throw Error('No duration given')
			}

			if (!options.hasOwnProperty('frequencyLow')) {
				throw Error('No minimum frequency given')
			}

			if (!options.hasOwnProperty('frequencyHigh')) {
				throw Error('No maximum frequency given')
			}

			switch (options.type) {
				case 'google':
				case 'c3':
				case 'json':
					if (!options.hasOwnProperty('data')) {
						throw Error("Options must include a 'data' key")
					}
					break
				case 'htmlTable':
					if (!options.hasOwnProperty('table')) {
						throw Error("Options must include a 'table' key")
					}
					break
				default:
					throw Error(`Invalid data type '${options.type}' given.`)
			}
		}

		/**
		 * Make a data wrapper of the appropriate class, instantiated with the
		 * appropriate data paramater (from options.data) and, optionally, make
		 * a visual callback (which may use options.chart, or other options if
		 * it's an HTML table visual callback).
		 * FIXME: Test somehow
		 * @private
		 * @param {AudioContext} context - the Web Audio context
		 * @param {AudioChartOptions} options - given by the user
		 */
		_wireUpStuff(context, options) {
			const assigned = AudioChart._assignWrapperCallback(options)
			const dataWrapper = new assigned.WrapperClass(assigned.dataSource)
			const callback = assigned.visualCallback

			const numberOfSeries = dataWrapper.numSeries()

			const seriesInfo = []
			for (let i = 0; i < numberOfSeries; i++ ) {
				seriesInfo.push({
					minimumDatum: dataWrapper.seriesMin(i),
					maximumDatum: dataWrapper.seriesMax(i),
					minimumFrequency: options.frequencyLow,
					maximumFrequency: options.frequencyHigh
				})
			}

			const frequencyPitchMapper = new FrequencyPitchMapper(seriesInfo)

			const sounder = new Sounder(context, numberOfSeries)

			this.player = new Player(
				options.duration,
				dataWrapper,
				frequencyPitchMapper,
				sounder,
				callback)

			if (options.chartContainer) {
				new KeyboardHandler(
					options.chartContainer,
					this.player)
			}
		}

		/**
		 * Works out which data source wrapper and visual callback (if requested)
		 * should be used with this chart.
		 * @param {AudioChartOptions} options - given by the user
		 * @returns {WrapperAndCallbackResults}
		 *	- data wrapper, data source and callback (if applicable) for this chart
		 * @private
		 */
		static _assignWrapperCallback(options) {
			const result = {
				'WrapperClass': null,
				'dataSource': null,
				'visualCallback': null
			}

			const chartTypeToWrapperClass = {
				google: GoogleDataWrapper,
				json: JSONDataWrapper,
				htmlTable: HTMLTableDataWrapper,
				c3: C3DataWrapper
			}

			const chartTypeToVisualCallbackMaker = {
				google: googleVisualCallbackMaker,
				c3: c3VisualCallbackMaker
			}

			switch (options.type) {
				case 'google':
				case 'json':
				case 'c3':
					result.WrapperClass = chartTypeToWrapperClass[options.type]
					result.dataSource = options.data
					if (options.hasOwnProperty('chart')) {
						result.visualCallback =
							chartTypeToVisualCallbackMaker[options.type](
								options.chart)
					}
					break
				case 'htmlTable':
					result.WrapperClass = HTMLTableDataWrapper
					result.dataSource = options.table
					if (options.hasOwnProperty('highlightClass')) {
						result.visualCallback = htmlTableVisualCallbackMaker(
							options.table,
							options.highlightClass)
					}
					break
			}

			return result
		}
	}

	exports.AudioChart = AudioChart
})(this)
